import { tns } from 'tiny-slider/src/tiny-slider';

/**
 * BCE Service Overview Module
 * 
 * Provides a service overview that can function in both grid and slider modes.
 * Uses TinySlider library for slider functionality when slider mode is enabled.
 * 
 * @class BceServiceOverview
 */
class BceServiceOverview {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
        this.sliderSelector = '.bce_service-slider';
        this.gridSelector = '.bce_service-grid';
        this.oldSelector = '.bce_service-overview';
        
        // Make tns globally available
        if (typeof window !== 'undefined') {
            window.tns = tns;
        }
    }

    /**
     * Initialize service overview elements
     * Destroys old elements and creates new slider or grid elements
     */
    init() {
        // Destroy old elements first
        this.destroyOldElements();
        
        // Initialize new slider elements
        const sliderElements = document.querySelectorAll(this.sliderSelector);
        sliderElements.forEach(element => {
            if (!this.instances.has(element)) {
                this.createSlider(element);
            }
        });
        
        // Initialize new grid elements
        const gridElements = document.querySelectorAll(this.gridSelector);
        gridElements.forEach(element => {
            if (!this.instances.has(element)) {
                this.createGrid(element);
            }
        });
    }

    /**
     * Destroy old service overview elements
     */
    destroyOldElements() {
        const oldElements = document.querySelectorAll(this.oldSelector);
        oldElements.forEach(element => {
            // Destroy any existing instances
            this.destroy(element);
            // Remove the element from DOM
            element.remove();
        });
    }

    /**
     * Create a slider instance
     * 
     * @param {HTMLElement} element - The container element with bce_service-slider class
     * @param {Object} options - Custom options for the slider
     * @returns {Object|null} Slider instance or null
     */
    createSlider(element, options = {}) {
        if (this.instances.has(element)) {
            return this.instances.get(element);
        }

        // Synchronous guard BEFORE the async setup (loadTinySlider + 500ms + 100ms).
        // Without this, a second init()/create() within that ~600ms window sees an
        // empty WeakMap and builds a second .main-slider-wrapper-4items → duplicated slider.
        this.instances.set(element, { mode: 'pending' });

        this.initializeSlider(element, options);
        return this.instances.get(element);
    }

    /**
     * Create a grid instance
     * 
     * @param {HTMLElement} element - The container element with bce_service-grid class
     * @returns {Object|null} Grid instance or null
     */
    createGrid(element) {
        if (this.instances.has(element)) {
            return this.instances.get(element);
        }

        // Grid mode - just mark as initialized
        this.instances.set(element, { mode: 'grid' });
        return this.instances.get(element);
    }

    /**
     * Initialize slider mode for service overview
     * 
     * @param {HTMLElement} element - The service overview container
     * @param {Object} options - Custom slider options
     */
    initializeSlider(element, options = {}) {
        // Hide element initially to prevent flicker
        element.style.opacity = '0';

        // Load TinySlider and initialize
        this.loadTinySlider(() => {
            setTimeout(() => {
                this.setupSlider(element, options);
            }, 500);
        });
    }

    /**
     * Setup the TinySlider instance
     * 
     * @param {HTMLElement} element - The service overview container
     * @param {Object} options - Custom slider options
     */
    setupSlider(element, options = {}) {
        // DOM-level guard: if a slider wrapper was already built for this element
        // (e.g. by a second, separately-bundled BCE instance with its own WeakMap),
        // do not build another one.
        if (element.querySelector('.main-slider-wrapper-4items')) {
            element.style.opacity = '1';
            return;
        }

        // Hide original wrapper completely
        const originalWrapper = element.querySelector('.main-slider-wrapper');
        if (originalWrapper) {
            originalWrapper.style.display = 'none !important';
            originalWrapper.style.visibility = 'hidden';
        }

        // Extract service items from original structure
        let originalSlides = element.querySelectorAll('.tns-item:not(.tns-slide-cloned)');
        
        // Fallback: look in nested slider
        if (originalSlides.length === 0) {
            const nestedSlider = element.querySelector('.main-slider');
            if (nestedSlider) {
                originalSlides = nestedSlider.querySelectorAll('.tns-item:not(.tns-slide-cloned)');
            }
        }
        
        // Additional fallback: look for any items with tns-item class
        if (originalSlides.length === 0) {
            originalSlides = element.querySelectorAll('[class*="tns-item"]:not([class*="cloned"])');
        }
        
        if (originalSlides.length === 0) {
            element.style.opacity = '1';
            return;
        }

        // Create completely new structure
        const newWrapper = document.createElement('div');
        newWrapper.className = 'main-slider-wrapper-4items';
        newWrapper.innerHTML = `
            <div class="main-slider-4items">
                ${Array.from(originalSlides).map(slide => slide.outerHTML).join('')}
            </div>
        `;

        // Insert new wrapper after original
        if (originalWrapper && originalWrapper.parentNode) {
            originalWrapper.parentNode.insertBefore(newWrapper, originalWrapper.nextSibling);
        } else {
            element.appendChild(newWrapper);
        }
        
        // Force hide original wrapper with CSS
        const style = document.createElement('style');
        style.textContent = `
            .bce_service-slider .main-slider-wrapper {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                height: 0 !important;
                overflow: hidden !important;
            }
        `;
        document.head.appendChild(style);

        // Initialize new TinySlider instance on duplicated content
        try {
            const defaultOptions = {
                container: newWrapper.querySelector('.main-slider-4items'),
                items: 4,
                slideBy: 1,
                autoplay: false,
                controls: true,
                controlsText: ['', ''],
                nav: false,
                mouseDrag: true,
                gutter: 20,
                responsive: {
                    320: { items: 1 },
                    600: { items: 2 },
                    900: { items: 3 },
                    1100: { items: 4 }
                }
            };

            const sliderOptions = { ...defaultOptions, ...options };
            const newSlider = window.tns(sliderOptions);

            // Style navigation buttons and show element
            setTimeout(() => {
                this.styleNavigationButtons(element, newWrapper);
                
                element.style.opacity = '1';
                element.classList.add('ready-4items');
                
                // Store instance
                this.instances.set(element, { 
                    mode: 'slider', 
                    slider: newSlider,
                    wrapper: newWrapper
                });

            }, 100);

        } catch (error) {
            element.style.opacity = '1';
        }
    }

    /**
     * Style the navigation buttons to match original design
     * 
     * @param {HTMLElement} element - The service overview container
     * @param {HTMLElement} newWrapper - The new wrapper element
     */
    styleNavigationButtons(element, newWrapper) {
        const originalPrev = element.querySelector('.slider-btn-prev');
        const originalNext = element.querySelector('.slider-btn-next');
        const newControls = newWrapper.querySelector('.tns-controls');
        const newPrev = newControls?.querySelector('[data-controls="prev"]');
        const newNext = newControls?.querySelector('[data-controls="next"]');

        // Copy styling from original buttons if they exist
        if (originalPrev && newPrev) {
            newPrev.className = originalPrev.className;
            newPrev.innerHTML = originalPrev.innerHTML;
        }

        if (originalNext && newNext) {
            newNext.className = originalNext.className;
            newNext.innerHTML = originalNext.innerHTML;
        }

        // Position controls properly
        if (newControls) {
            // Copy styling from original controls container if it exists
            const originalControlsContainer = element.querySelector('.tns-controls');
            if (originalControlsContainer) {
                newControls.className = originalControlsContainer.className;
            }

            // Move controls after the slider container
            const sliderContainer = newWrapper.querySelector('.main-slider-4items');
            if (sliderContainer && sliderContainer.parentNode) {
                sliderContainer.parentNode.appendChild(newControls);
            }
        }
    }

    /**
     * Load TinySlider library dynamically
     * 
     * @param {Function} callback - Callback function to execute after loading
     */
    loadTinySlider(callback) {
        // TinySlider is now bundled and globally available
        if (typeof window.tns !== 'undefined') {
            callback();
            return;
        }
        
        // Fallback: tns should be available immediately since it's imported
        callback();
    }

    /**
     * Destroy a service overview instance
     * 
     * @param {HTMLElement} element - The service overview container
     */
    destroy(element) {
        const instance = this.instances.get(element);
        if (instance) {
            if (instance.mode === 'slider' && instance.slider) {
                try {
                    instance.slider.destroy();
                } catch (error) {
                    // Ignore destroy errors
                }
            }
            if (instance.wrapper) {
                instance.wrapper.remove();
            }
            this.instances.delete(element);
        }
    }


    /**
     * Get instance for external access
     * 
     * @param {HTMLElement} element - The service overview container
     * @returns {Object|null} Service overview instance or null
     */
    getInstance(element) {
        return this.instances.get(element);
    }

    /**
     * Create method for BCE system compatibility
     * Called by BCE system for individual elements
     * 
     * @param {HTMLElement} element - The element to initialize
     */
    create(element) {
        if (element.classList.contains('bce_service-slider')) {
            this.createSlider(element);
        } else if (element.classList.contains('bce_service-grid')) {
            this.createGrid(element);
        }
    }
}

export default new BceServiceOverview();